<script setup lang="ts">
/**
 * The panel's button — the behaviour this app took from `@axon/ui`'s `Button`,
 * kept when the React design system could no longer be mounted here (ADR-0247).
 *
 * `type` DEFAULTS TO "button", so a control inside the pre-chat form does not
 * submit it. `busy` keeps the label rendered and the control un-clickable while
 * the action runs, and draws a spinner named by `busyLabel`.
 *
 * `variant`, `size` and `block` become `ow-button--*` classes. `widget.css`
 * paints by the call site's own `.ow-*` class today, exactly as it did when
 * these were Tailwind utilities nothing here compiled; the modifiers are there
 * so a rule can key on them without another component change.
 *
 * Every prop and event is DECLARED rather than left to fall through, so a typo
 * at a call site is a vue-tsc error (`strictTemplates`) instead of an attribute
 * nobody reads. `click` is re-emitted from the native one, and a disabled
 * button does not fire it — which is the whole of the busy guard.
 */
import OwSpinner from './OwSpinner.vue';

withDefaults(
  defineProps<{
    readonly type?: 'button' | 'submit' | undefined;
    readonly variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | undefined;
    readonly size?: 'sm' | 'md' | 'lg' | undefined;
    readonly block?: boolean | undefined;
    readonly disabled?: boolean | undefined;
    /** Keeps the label rendered and the control un-clickable while the action runs. */
    readonly busy?: boolean | undefined;
    /** Required whenever `busy` can become true — the spinner needs a translated name. */
    readonly busyLabel?: string | undefined;
    /**
     * Tooltip. Present for the language switcher, whose visible label is an
     * endonym ("EN", "Укр") that names the language it switches TO but not what
     * the control does. It is a tooltip and NOT the accessible name — the
     * endonym is already that — which is why this is `title` and not
     * `aria-label`.
     */
    readonly title?: string | undefined;
  }>(),
  { type: 'button', variant: 'secondary', size: 'md', block: false, disabled: false, busy: false },
);

const emit = defineEmits<{ click: [] }>();
</script>

<template>
  <button
    :type="type"
    :class="[
      'ow-button',
      `ow-button--${variant}`,
      `ow-button--${size}`,
      { 'ow-button--block': block },
    ]"
    :disabled="disabled || busy"
    :aria-busy="busy || undefined"
    :title="title"
    @click="emit('click')"
  >
    <OwSpinner v-if="busy" size="sm" :label="busyLabel" />
    <slot />
  </button>
</template>
